import React, { useState, useEffect, useRef } from 'react'
import { FormCheck } from 'react-bootstrap'
import { autorun } from 'mobx'
import {
  i18nTranslate,
  trackGTMEvents,
  getProductsForAgreementSection,
  isExpressCheckout,
  isLATAM,
  enableTimeZoneDateConversion,
  getImageFromProperty,
  currencyFormat,
  isExternalGPSCreditCardPaymentName,
  isPaymetricGPSCreditCardOption,
  convertToBoolean,
} from 'src/utils'
import { APPConfig } from 'config/appConfig'
import {
  FormInputLabels,
  dateFormatDeps,
  isFlexibleSubscriptionFlagEnabled,
  isMultipleSubscriptionFlagEnabled,
  isSubscriptionFlagEnabled,
  getSubscriptionProcessingDate,
  productDeps,
} from 'src/deps'
import {
  addressContainer,
  cartContainer,
  checkoutContainer,
  customerContainer,
  multipleSubscriptionContainer,
  nuskinSubscriptionContainer,
} from 'src/models'
import { IS_BROWSER, getMonthStringList } from 'src/utils'
import ExpressSectionHeader from '../../ExpressSectionHeader'
import linkFromContentStack from 'src/deps/LinkFromContantStack'
import { CommonForm } from 'src/views/components'
import { renderSubscriptionField } from './formInput'
import { schema, subscriptionNameRegex } from './validationSchema'
import './styles.scss'
import { getDefaultProcessingDate } from '../../CartBlock/deps'
import {
  SubscriptionTodayOrderTile,
  SubscriptionNextOrderTile,
} from './SubscriptionOrderTiles'
import parse from 'html-react-parser'

/** A saved/applied payment is a card if typed CREDITCARD/DEBITCARD, or (GPS markets)
 *  its name matches a currently configured external or Paymetric GPS credit-card
 *  processor — reusing the same resolution Checkout.js already relies on, instead
 *  of guessing from incidental metadata (NGE1-5306). */
const isCardLikePayment = payment => {
  const type = (payment?.type || '').toLowerCase()
  if (type === 'creditcard') {
    return true
  }
  if (type !== 'gps') {
    return false
  }
  const gpsCreditCardOptions =
    checkoutContainer?.globalPaymentResponse?.CREDITCARD || []
  return (
    isExternalGPSCreditCardPaymentName(payment?.name, gpsCreditCardOptions) ||
    isPaymetricGPSCreditCardOption({ name: payment?.name })
  )
}

/**
 * Resolves whether the credit-card payment agreement applies. Shows the checkbox
 * when credit card is explicitly selected OR a credit/debit card is listed on the
 * profile/cart. GPS markets wrap saved cards under a generic type ("GPS") and, for
 * "plain CVV" processors (e.g. Braintree), set selectedPaymentType to the
 * processor's own name instead of 'creditcard' — matching only on selectedType
 * missed these cards and hid the required T&C checkbox (NGE1-5306).
 *
 * All observables are read up-front so the enclosing autorun tracks them and
 * re-runs when GPS saved cards load asynchronously.
 */
function resolveIsCreditCardPayment(selectedType = '') {
  if (selectedType === 'creditcard') {
    return true
  }
  if (selectedType) {
    const gpsCreditCardOptions =
      checkoutContainer?.globalPaymentResponse?.CREDITCARD || []
    return (
      isExternalGPSCreditCardPaymentName(selectedType, gpsCreditCardOptions) ||
      isPaymetricGPSCreditCardOption({ name: selectedType })
    )
  }
  /** No explicit selection yet (e.g. GPS saved cards still loading asynchronously
   *  before a tab/card is chosen) — fall back to whatever is listed. */
  const savedPayments = customerContainer?.paymentResponse?.payments || []
  const appliedPayments = cartContainer?.cartResponse?.payments || []
  return (
    savedPayments.some(isCardLikePayment) ||
    appliedPayments.some(isCardLikePayment)
  )
}

function SubscriptionAccordion(props) {
  const [processingDate, setProcessingDate] = useState('')
  const [processingMonth, setProcessingMonth] = useState('')
  const [nextProcDate, setNextProcDate] = useState('')
  const [termsCheckbox, setTermsCheckbox] = useState(false)
  const [subscriptionName, setSubscriptionName] = useState('')
  const [isPaymentAgreementChecked, setIsPaymentAgreementChecked] =
    useState(false)
  const [links, setLinks] = useState({})
  const initialShowAgreement = isExpressCheckout() ? true : false
  const [showAgreement, setShowAgreement] = useState(initialShowAgreement)
  const termsAndLinksCTA =
    links.retailCustomerLinks?.subscriptionAgreement || '#'

  /** Payment-agreement checkbox is credit-card-specific ("card saved on my Subscription will be charged").
   *  Kept in state and refreshed by the autorun so it reacts to a listed/applied credit card
   *  (incl. GPS "GPS"-typed cards), not just an explicit selectedPaymentType (NGE1-5306). */
  const [isCreditCardPayment, setIsCreditCardPayment] = useState(() =>
    resolveIsCreditCardPayment(
      (checkoutContainer?.selectedPaymentType || '').toLowerCase()
    )
  )

  /** Reset the payment-agreement state whenever the checkbox is hidden so a returning
   *  creditcard selection always starts unchecked. */
  useEffect(() => {
    if (!isCreditCardPayment && isPaymentAgreementChecked) {
      setIsPaymentAgreementChecked(false)
    }
  }, [isCreditCardPayment, isPaymentAgreementChecked])

  /** Ref keeps latest props.termsCheck without retriggering the autorun below. */
  const termsCheckPropRef = useRef(props?.termsCheck)
  termsCheckPropRef.current = props?.termsCheck

  /** Single autorun tracks both React state (via deps) and MobX observables. */
  useEffect(() => {
    const disposer = autorun(() => {
      const currentPaymentType = (
        checkoutContainer?.selectedPaymentType || ''
      ).toLowerCase()
      const isCreditCardCurrent = resolveIsCreditCardPayment(currentPaymentType)
      setIsCreditCardPayment(isCreditCardCurrent)

      /** When checkbox is hidden (non-creditcard), treat the payment agreement as satisfied. */
      const isPaymentAgreementSatisfied =
        !isCreditCardCurrent || isPaymentAgreementChecked

      if (isMultipleSubscriptionFlagEnabled() || false) {
        termsCheckPropRef.current?.(
          termsCheckbox &&
            isPaymentAgreementSatisfied &&
            subscriptionName !== '' &&
            multipleSubscriptionContainer.isSubscriptionNameValid
        )
      } else if (isFlexibleSubscriptionFlagEnabled() || false) {
        termsCheckPropRef.current?.(
          termsCheckbox && isPaymentAgreementSatisfied
        )
      } else {
        termsCheckPropRef.current?.(termsCheckbox)
      }
    })
    return () => disposer()
  }, [termsCheckbox, isPaymentAgreementChecked, subscriptionName])

  useEffect(() => {
    //CX16-9904 - Flexible Subscription - Start
    const isToShowNewDesign = isSubscriptionFlagEnabled() || false

    let currentDate = new Date()
    let month = currentDate.getMonth()
    let date = currentDate.getDate()
    // Set nextDate as 2 if date is 1 amd 28 if 29/30/31
    // let nextDate = [29, 30, 31].indexOf(date) > -1 ? 28 : date === 1 ? 2 : date
    const defaultProcessingDate =
      APPConfig?.getAppConfig()?.defaultProcessingDate || {}

    let nextDate =
      (nuskinSubscriptionContainer.isToShowBlackOut &&
        props?.defaultProcessingDate) ||
      defaultProcessingDate?.[date] ||
      date
    let ordinalDate = date => {
      return (
        date +
        (date % 10 == 1 && date != 11
          ? 'st'
          : date % 10 == 2 && date != 12
            ? 'nd'
            : date % 10 == 3 && date != 13
              ? 'rd'
              : 'th')
      )
    }

    const consumableDetailsList = getConsumableDetails()
    const hasNormalSubscriptionProduct =
      cartContainer.hasNormalSubscriptionProduct()

    const leastConsumableFrequency =
      consumableDetailsList.length === 0
        ? 1
        : consumableDetailsList.reduce((minFreq, detail) => {
            const freq = Number(detail?.frequencyPeriod) || 1
            return Math.min(minFreq, freq)
          }, Number.MAX_SAFE_INTEGER)

    const calculatedProcessDate = hasNormalSubscriptionProduct
      ? currentDate
      : new Date(
          new Date().setMonth(currentDate.getMonth() + leastConsumableFrequency)
        )
    const calculatedProcessTime = hasNormalSubscriptionProduct
      ? Date.now()
      : new Date().setMonth(new Date().getMonth() + leastConsumableFrequency)

    //CX16-9904 - Flexible Subscription - Start
    if (isToShowNewDesign) {
      const isMultipleSubscriptionEnabled =
        isMultipleSubscriptionFlagEnabled() || false
      const processingDate = dateFormatDeps?.getDateAsStringWithDay(
        calculatedProcessDate
      )
      let enableTimeZoneDateFormat = enableTimeZoneDateConversion() || false
      const subscriptionProcessingDate =
        isMultipleSubscriptionEnabled && enableTimeZoneDateFormat
          ? getSubscriptionProcessingDate(calculatedProcessTime)
          : processingDate
      setProcessingDate(subscriptionProcessingDate)
    } else {
      setProcessingDate(ordinalDate(date))
    }
    //CX16-9904 - Flexible Subscription - End
    setNextProcDate(ordinalDate(nextDate))
    setProcessingMonth(getMonthStringList()[month])
    setLinks(new FormInputLabels())
  }, [])

  const navigateToTermsAndConditions = async e => {
    e.preventDefault()
    if (IS_BROWSER) {
      if (linkFromContentStack?.termAndConditionLink) {
        window.open(linkFromContentStack?.termAndConditionLink)
      } else {
        const termAndConditionLink =
          await linkFromContentStack.fetchTermsConditionsLink()
        window.open(termAndConditionLink)
      }
    }
  }
  const handleKeyPress = e => {
    if (e.keyCode === 13) {
      navigateToTermsAndConditions(e)
    }
  }
  const handleValueChange = props => {
    const { event, setFieldValue = function () {} } = props || {}
    const { name = '', value = '' } = event?.target || {}
    const regex = subscriptionNameRegex || ''
    const subscriptionNameDiv = document.querySelector(
      "[name='subscriptionName']"
    )
    setFieldValue(name, value)
    multipleSubscriptionContainer.subscriptionGroupName = value
    setSubscriptionName(value)
    let validRegex = new RegExp(regex)?.test(value)
    multipleSubscriptionContainer.isSubscriptionNameValid = validRegex
    if (validRegex) {
      if (subscriptionNameDiv?.classList?.contains('is-invalid'))
        subscriptionNameDiv.classList.remove('is-invalid')
      subscriptionNameDiv.classList.add('is-valid')
    } else {
      if (subscriptionNameDiv?.classList?.contains('is-valid'))
        subscriptionNameDiv.classList.remove('is-valid')
      subscriptionNameDiv.classList.add('is-invalid')
    }
  }

  const formInputJson = renderSubscriptionField({
    handleValueChange: handleValueChange,
    subscriptionName,
  })

  const subsCustomerSupport =
    APPConfig?.getAppConfig()?.subscriptionCustomerSupport || ''
  const subscriptionCheckoutDisclaimerRichText = convertToBoolean(
    APPConfig?.getAppConfig()?.subscriptionCheckoutDisclaimer
  )

  const subscriptionDisclaimerRichText = i18nTranslate(
    'subscriptions.checkout.termsAndConditionsText',
    'Subsequent orders under this Subscription will automatically process on the {{nextProcDate}} day of the month based on the frequency you have selected.'
  ).replace('{{nextProcDate}}', nextProcDate)

  const processOnText = i18nTranslate(
    'subscriptionAccordion.processOnText',
    'Your first order, will be processed on'
  )

  const nextOrderProcessingText = i18nTranslate(
    'subscriptionAccordion.nextOrderProcessingText',
    'Subsequent orders under this Subscription will automatically process on the'
  )

  const frequencySelectedText = i18nTranslate(
    'subscriptionAccordion.frequencySelectedText',
    'day of the month based on the frequency you have selected.'
  )

  const creditCardChargesText = i18nTranslate(
    'subscriptionAccordion.creditCardChargesText',
    'The credit card used to purchase this subscription will be charged each processing date.'
  )

  const chargesText = i18nTranslate(
    'subscriptionAccordion.chargesText',
    'Applicable taxes and shipping charges may vary.'
  )

  const cancellationText = i18nTranslate(
    'subscriptionAccordion.cancellationText',
    'You may change or cancel your Subscriptions at any time by logging into your Nu Skin account and accessing your Subscription Dashboard or by contacting Customer Support at '
  )

  const textProcessTimesApply = i18nTranslate(
    'subscriptionAccordion.processTimesText',
    ' (processing times apply).'
  )

  const termsWarningText = i18nTranslate(
    'subscriptionAccordion.termsWarningText',
    "Before we can process your Subscription, you must agree to our Subscription Terms and Conditions. Please review these terms carefully, as they place limits on Nu Skin's liability and contain important information about your legal rights and obligations. You may download a copy"
  )

  const hereLink = i18nTranslate('subscriptionAccordion.hereLink', 'here.')

  const agreementText = i18nTranslate(
    'subscriptionAccordion.agreementText',
    'I have read, understand, and agree to the'
  )

  const termsAndConditionLink = i18nTranslate(
    'subscriptionAccordion.termsAndConditionLink',
    'Subscription Terms and Conditions.'
  )

  const paymentAgreementCheckboxLabel = i18nTranslate(
    'cartDrawer.paymentAgreementCheckboxLabel',
    'I agree the credit card saved on my Subscription will be charged each processing date'
  )

  const isMultipleSubscriptionEnabled =
    isMultipleSubscriptionFlagEnabled() || false
  const isToShowNewDesign = isSubscriptionFlagEnabled() || false
  const hasNormalSubscriptionProduct =
    cartContainer.hasNormalSubscriptionProduct()

  /**
   * Returns consumable subscription details reconstructed from cartResponse.properties.consumablePdtIds.
   * For each parsed skuId, searches cart item skus to enrich with name, imageURL, price, quantity and frequencyPeriod.
   * Iterates only direct item.skus array; does not traverse nested bundle sub-products.
   * MDIGI-13254: consumableSubscriptionDetails property does not exist on nuskinSubscriptionContainer;
   * always reconstructs from properties to ensure page refresh consistency.
   */
  const getConsumableDetails = React.useCallback(() => {
    const consumablePdtIds =
      cartContainer.cartResponse?.properties?.consumablePdtIds || ''
    if (!consumablePdtIds) return []

    const allCartItems = cartContainer.cartResponse?.items || []

    /** Build skuId -> { sku, item } map across all items and their sub-product skus */
    const skuMap = {}
    allCartItems.forEach(item => {
      ;(item?.skus || []).forEach(sku => {
        const id = sku?.skuId
        if (id && !skuMap[id]) {
          skuMap[id] = { sku }
        }
      })
    })

    return consumablePdtIds
      .split(',')
      .filter(Boolean)
      .map(entry => {
        const [productId, skuId, qty, freq] = entry.split('~')
        const found = skuMap[skuId] || {}
        const sku = found.sku || {}
        const rawPrice = sku?.priceAfterMarkdown || sku?.price || 0
        return {
          productId: productId || '',
          skuId: skuId || '',
          name: sku?.skuProperties?.name || '',
          quantity: Number(qty) || 1,
          frequencyPeriod: Number(freq) || 1,
          imageURL: getImageFromProperty(sku?.skuProperties),
          price: rawPrice ? currencyFormat({ price: rawPrice }) : '',
        }
      })
  }, [
    cartContainer.cartResponse?.properties?.consumablePdtIds,
    cartContainer.cartResponse?.items,
  ])

  const renderSubscriptionDisclaimerText = () => {
    if (subscriptionCheckoutDisclaimerRichText)
      return <div className="mb-2">{parse(subscriptionDisclaimerRichText)}</div>

    return (
      <>
        <div className="mb-2">
          <span className="font-weight-bold">{nextOrderProcessingText}</span>{' '}
          <span className="font-weight-bold">{nextProcDate}</span>{' '}
          <span className="font-weight-bold">{frequencySelectedText}</span>{' '}
          <span>
            {creditCardChargesText} {chargesText}
          </span>
        </div>
        <p className="mb-2">
          {cancellationText}
          {subsCustomerSupport}
          {textProcessTimesApply}
        </p>
        <div className="mb-2">
          <span>{termsWarningText}</span>{' '}
          <span
            className="sub-acc-terms"
            role="link"
            tabIndex="0"
            onClick={navigateToTermsAndConditions}
            onKeyDown={handleKeyPress}
            target="_blank"
            rel="noreferrer noopener">
            <u>{hereLink}</u>
          </span>
        </div>
      </>
    )
  }

  const consumableSkuIds = cartContainer.getSubConsumableSkuIdSet()

  const normalSubscriptionItems = (
    cartContainer.cartResponse?.items || []
  ).filter(
    item =>
      item?.isSubscription === true &&
      !cartContainer.isConsumableSubscriptionItem(item, consumableSkuIds)
  )

  const consumableDetailsList = React.useMemo(
    () => getConsumableDetails(),
    [getConsumableDetails]
  )

  const leastConsumableFrequency = React.useMemo(() => {
    if (consumableDetailsList.length === 0) return 1
    return consumableDetailsList.reduce((minFreq, detail) => {
      const freq = Number(detail?.frequencyPeriod) || 1
      return Math.min(minFreq, freq)
    }, Number.MAX_SAFE_INTEGER)
  }, [consumableDetailsList])

  const effectiveMonth =
    hasNormalSubscriptionProduct ||
    leastConsumableFrequency === Number.MAX_SAFE_INTEGER
      ? processingMonth
      : getMonthStringList()[
          new Date(
            new Date().setMonth(
              new Date().getMonth() + leastConsumableFrequency
            )
          ).getMonth()
        ]

  const gtmData = cartContainer?.cartResponse || {}
  const checkoutProducts = getProductsForAgreementSection(gtmData?.items) || {}
  const shippingAddress =
    isLATAM() && addressContainer?.addressData?.length === 0
  return (
    <div className="sub-acc-container">
      {/* <div>
        Your first order will be processed on {processingMonth} {processingDate}
        .
      </div>
      <div>
        Your Subscription order will process on the {nextProcDate} of every
        month. If any changes need to be made to a future Subscription or you
        wish to cancel at anytime, please manage your Subscription.
      </div> */}

      {isExpressCheckout() && shippingAddress && (
        <>
          <div className="subscription-heading">
            {i18nTranslate(
              'checkout.subscriptionAgreementHeading',
              'View Subscription Agreement'
            )}
          </div>
          <div className="emptyShipping-method p-3 mb-2 subscriptionAgreement">
            {i18nTranslate(
              'checkout.subscriptionAgreement',
              'Subscription Agreement will be available once you enter shipping address'
            )}
          </div>
        </>
      )}

      {isExpressCheckout() && !shippingAddress && (
        <ExpressSectionHeader
          title={i18nTranslate(
            'checkout.subscriptionAgreementHeading',
            'View Subscription Agreement'
          )}
          isOpen={showAgreement}
          onClick={() => setShowAgreement(!showAgreement)}
        />
      )}

      {(!isExpressCheckout() || showAgreement) && !shippingAddress && (
        <>
          {isMultipleSubscriptionEnabled && (
            <CommonForm
              schema={schema()}
              formTypeJson={formInputJson}
              classNameValue={'subscription-name'}
              handleValueChange={handleValueChange}
            />
          )}
          <div className="mb-2">
            <span>{processOnText}</span>
            {isToShowNewDesign ? (
              <>
                :<br />
              </>
            ) : (
              ' '
            )}
            <span className="font-weight-bold text-capitalize">
              {!isToShowNewDesign && `${effectiveMonth} `}
              {processingDate}
              {!isToShowNewDesign && '.'}
            </span>
          </div>
          {productDeps.isEnableSubProductsSubscribeSelect() &&
            hasNormalSubscriptionProduct &&
            consumableDetailsList.length > 0 && (
              <SubscriptionTodayOrderTile
                normalSubscriptionItems={normalSubscriptionItems}
              />
            )}
          {productDeps.isEnableSubProductsSubscribeSelect() &&
            consumableDetailsList.length > 0 && (
              <SubscriptionNextOrderTile
                normalSubscriptionItems={normalSubscriptionItems}
                consumableDetailsList={consumableDetailsList}
              />
            )}
          {renderSubscriptionDisclaimerText()}
        </>
      )}
      {!shippingAddress && (
        <>
          <FormCheck className="sub-acc-checkbox">
            <FormCheck.Input
              className="term-condition"
              type="checkbox"
              id="terms_checkbox"
              onChange={e => {
                setTermsCheckbox(e.target.checked)
                trackGTMEvents({
                  event: 'subscriptionAgree',
                  products: checkoutProducts,
                  status: e?.target?.checked ? 'optin' : 'optout',
                  type: 'checkout subscription agreement',
                })
              }}
            />
            <FormCheck.Label htmlFor="terms_checkbox">
              {agreementText}{' '}
              <p
                className="sub-acc-terms"
                role="link"
                tabIndex="0"
                onClick={navigateToTermsAndConditions}
                onKeyDown={handleKeyPress}
                target="_blank"
                rel="noreferrer noopener">
                <u>{termsAndConditionLink}</u>
              </p>
            </FormCheck.Label>
          </FormCheck>
          {isToShowNewDesign && isCreditCardPayment && (
            <FormCheck className="sub-acc-checkbox">
              <FormCheck.Input
                checked={isPaymentAgreementChecked}
                onChange={e => {
                  setIsPaymentAgreementChecked(e.target.checked)
                  trackGTMEvents({
                    event: 'subscriptionCreditCardAgree',
                    products: checkoutProducts,
                    status: e?.target?.checked ? 'optin' : 'optout',
                    type: 'checkout subscription agreement',
                  })
                }}
                type="checkbox"
                className="payment-agreement-checkbox"
                id="payment_agreement_checkbox"
              />
              <FormCheck.Label
                className="payment_agreement_label"
                htmlFor="payment_agreement_checkbox">
                {paymentAgreementCheckboxLabel}
              </FormCheck.Label>
            </FormCheck>
          )}
        </>
      )}
    </div>
  )
}

export { SubscriptionAccordion }
export default SubscriptionAccordion
